/**
 * Admin Plans Page
 * გამოწერის გეგმების ადმინისტრირების გვერდი
 */

import { useState } from 'react';
import { useNotification } from '@aiia/portal-core';
import {
  useAdminPlans,
  useCreatePlan,
  useUpdatePlan,
  useDeletePlan,
  useUpdateAdminPlanFeatures,
} from '../../hooks/useAdminPlans';
import type { PlanAdminResponse, AdminPlanFeature } from '../../features/api/adminPlans';

export default function PlansPage() {
  const notification = useNotification();
  const { plans, isLoading, refetch } = useAdminPlans();
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();
  const updateAdminPlanFeatures = useUpdateAdminPlanFeatures();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanAdminResponse | null>(null);

  // Create/Edit form
  const [formCode, setFormCode] = useState('');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formMonthlyPrice, setFormMonthlyPrice] = useState(0);
  const [formYearlyPrice, setFormYearlyPrice] = useState(0);
  const [formActive, setFormActive] = useState(true);
  const [formPublic, setFormPublic] = useState(true);
  const [formSortOrder, setFormSortOrder] = useState(0);

  // Features form
  const [editingFeatures, setEditingFeatures] = useState<AdminPlanFeature[]>([]);

  const resetForm = () => {
    setFormCode('');
    setFormName('');
    setFormDescription('');
    setFormMonthlyPrice(0);
    setFormYearlyPrice(0);
    setFormActive(true);
    setFormPublic(true);
    setFormSortOrder(0);
  };

  const handleCreate = async () => {
    if (!formCode || !formName) {
      notification.error('Code and Name are required');
      return;
    }
    try {
      await createPlan.mutateAsync({
        code: formCode.toUpperCase(),
        name: formName,
        description: formDescription,
        monthlyPrice: formMonthlyPrice,
        yearlyPrice: formYearlyPrice,
        active: formActive,
        publicPlan: formPublic,
      });
      notification.success('Plan created successfully');
      setShowCreateModal(false);
      resetForm();
      refetch();
    } catch {
      notification.error('Failed to create plan');
    }
  };

  const handleEdit = (plan: PlanAdminResponse) => {
    setSelectedPlan(plan);
    setFormName(plan.name);
    setFormDescription(plan.description || '');
    setFormMonthlyPrice(plan.monthlyPrice);
    setFormYearlyPrice(plan.yearlyPrice);
    setFormActive(plan.active);
    setFormPublic(plan.publicPlan);
    setFormSortOrder(plan.sortOrder);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedPlan) return;
    try {
      await updatePlan.mutateAsync({
        code: selectedPlan.code,
        data: {
          name: formName,
          description: formDescription,
          monthlyPrice: formMonthlyPrice,
          yearlyPrice: formYearlyPrice,
          active: formActive,
          publicPlan: formPublic,
          sortOrder: formSortOrder,
        },
      });
      notification.success('Plan updated successfully');
      setShowEditModal(false);
      resetForm();
      refetch();
    } catch {
      notification.error('Failed to update plan');
    }
  };

  const handleDelete = async (plan: PlanAdminResponse) => {
    if (plan.subscriptionCount > 0) {
      notification.error(`Cannot delete plan with ${plan.subscriptionCount} active subscriptions`);
      return;
    }
    if (!confirm(`Are you sure you want to delete "${plan.name}"?`)) return;
    try {
      await deletePlan.mutateAsync(plan.code);
      notification.success('Plan deleted successfully');
      refetch();
    } catch {
      notification.error('Failed to delete plan');
    }
  };

  const handleEditFeatures = (plan: PlanAdminResponse) => {
    setSelectedPlan(plan);
    setEditingFeatures([...plan.features]);
    setShowFeaturesModal(true);
  };

  const handleFeatureChange = (index: number, field: 'limitValue' | 'unlimited', value: number | boolean) => {
    setEditingFeatures(prev => {
      const updated = [...prev];
      if (field === 'unlimited') {
        updated[index] = { ...updated[index], unlimited: value as boolean };
      } else {
        updated[index] = { ...updated[index], limitValue: value as number };
      }
      return updated;
    });
  };

  const handleSaveFeatures = async () => {
    if (!selectedPlan) return;
    try {
      await updateAdminPlanFeatures.mutateAsync({
        code: selectedPlan.code,
        data: {
          features: editingFeatures.map(f => ({
            featureCode: f.featureCode,
            limitValue: f.limitValue,
            unlimited: f.unlimited,
          })),
        },
      });
      notification.success('Features updated successfully');
      setShowFeaturesModal(false);
      refetch();
    } catch {
      notification.error('Failed to update features');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="text-gray-600">Manage subscription plans and their features</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Create Plan
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan: PlanAdminResponse) => (
          <div key={plan.code} className={`bg-white rounded-lg shadow-lg p-6 ${!plan.active ? 'opacity-60' : ''}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                <span className="text-sm text-gray-500">{plan.code}</span>
              </div>
              <div className="flex gap-1">
                {plan.active ? (
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
                ) : (
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Inactive</span>
                )}
                {plan.publicPlan && (
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Public</span>
                )}
              </div>
            </div>

            {plan.description && (
              <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
            )}

            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Monthly</span>
                <span className="font-bold">{plan.monthlyPrice} {plan.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Yearly</span>
                <span className="font-bold">{plan.yearlyPrice} {plan.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Subscribers</span>
                <span className="font-bold">{plan.subscriptionCount}</span>
              </div>
            </div>

            <div className="border-t pt-4 mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Features ({plan.features.length})</h4>
              <div className="text-xs text-gray-500 space-y-1 max-h-32 overflow-auto">
                {plan.features.slice(0, 5).map((f: AdminPlanFeature) => (
                  <div key={f.featureCode} className="flex justify-between">
                    <span>{f.displayName}</span>
                    <span className="font-medium">
                      {f.unlimited ? 'Unlimited' : f.limitValue}
                    </span>
                  </div>
                ))}
                {plan.features.length > 5 && (
                  <div className="text-blue-600">+{plan.features.length - 5} more...</div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(plan)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                onClick={() => handleEditFeatures(plan)}
                className="flex-1 px-3 py-2 text-sm border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                Features
              </button>
              <button
                onClick={() => handleDelete(plan)}
                disabled={plan.subscriptionCount > 0}
                className="px-3 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-4">Create Plan</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value.toUpperCase())}
                  placeholder="e.g., STARTER"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price</label>
                  <input
                    type="number"
                    value={formMonthlyPrice}
                    onChange={(e) => setFormMonthlyPrice(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Price</label>
                  <input
                    type="number"
                    value={formYearlyPrice}
                    onChange={(e) => setFormYearlyPrice(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formActive}
                    onChange={(e) => setFormActive(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formPublic}
                    onChange={(e) => setFormPublic(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Public</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createPlan.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createPlan.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-4">Edit Plan: {selectedPlan.code}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price</label>
                  <input
                    type="number"
                    value={formMonthlyPrice}
                    onChange={(e) => setFormMonthlyPrice(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Price</label>
                  <input
                    type="number"
                    value={formYearlyPrice}
                    onChange={(e) => setFormYearlyPrice(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input
                  type="number"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formActive}
                    onChange={(e) => setFormActive(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formPublic}
                    onChange={(e) => setFormPublic(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Public</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowEditModal(false); resetForm(); }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updatePlan.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {updatePlan.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Features Modal */}
      {showFeaturesModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold">Edit Features: {selectedPlan.name}</h2>
              <button onClick={() => setShowFeaturesModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {editingFeatures.map((feature, index) => (
                  <div key={feature.featureCode} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{feature.displayName}</div>
                      <div className="text-xs text-gray-500">{feature.featureCode}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={feature.unlimited}
                          onChange={(e) => handleFeatureChange(index, 'unlimited', e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Unlimited</span>
                      </label>
                      <input
                        type="number"
                        value={feature.limitValue}
                        onChange={(e) => handleFeatureChange(index, 'limitValue', parseInt(e.target.value) || 0)}
                        disabled={feature.unlimited}
                        className="w-24 px-2 py-1 border border-gray-300 rounded disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowFeaturesModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFeatures}
                disabled={updateAdminPlanFeatures.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {updateAdminPlanFeatures.isPending ? 'Saving...' : 'Save Features'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
